// Iter36 — Two-level language system.
//
// Level 1 (UI/Chat language): controls all UI strings, SITREP system prompt,
// chat input/output language. Stored in `localStorage.sitroom_locale` (so the
// login screen localises before the user is known) AND on the user record via
// PATCH /api/users/me. Loaded BEFORE React mounts to avoid a flash.
//
// Level 2 (Content display language): controls how incident titles/descriptions
// /comments are rendered. Originals are never mutated — translated text is
// fetched from POST /api/translate (Claude Haiku) and cached in-memory.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import en from "./en.json";
import de from "./de.json";
import hu from "./hu.json";

export const SUPPORTED_LOCALES = ["en", "de", "hu"];
export const SUPPORTED_DISPLAY_LOCALES = ["original", "en", "de", "hu"];

const BUNDLES = { en, de, hu };

const STORAGE_LOCALE = "sitroom_locale";
const STORAGE_DISPLAY_LOCALE = "sitroom_display_locale";

const isBrowser = typeof window !== "undefined";

const detectNavigatorLocale = () => {
  if (!isBrowser || !navigator) return "en";
  const nav = (navigator.language || navigator.userLanguage || "en").toLowerCase();
  if (nav.startsWith("de")) return "de";
  if (nav.startsWith("hu")) return "hu";
  return "en";
};

export const readStoredLocale = () => {
  if (!isBrowser) return "en";
  const v = localStorage.getItem(STORAGE_LOCALE);
  if (SUPPORTED_LOCALES.includes(v)) return v;
  return detectNavigatorLocale();
};

export const readStoredDisplayLocale = () => {
  if (!isBrowser) return "original";
  const v = localStorage.getItem(STORAGE_DISPLAY_LOCALE);
  if (SUPPORTED_DISPLAY_LOCALES.includes(v)) return v;
  return "original";
};

const writeStoredLocale = (v) => {
  if (isBrowser && SUPPORTED_LOCALES.includes(v)) localStorage.setItem(STORAGE_LOCALE, v);
};
const writeStoredDisplayLocale = (v) => {
  if (isBrowser && SUPPORTED_DISPLAY_LOCALES.includes(v)) localStorage.setItem(STORAGE_DISPLAY_LOCALE, v);
};

const resolve = (bundle, path) => {
  const parts = path.split(".");
  let cur = bundle;
  for (const p of parts) {
    if (cur == null) return undefined;
    cur = cur[p];
  }
  return cur;
};

const interpolate = (str, vars) => {
  if (typeof str !== "string" || !vars) return str;
  return str.replace(/\{(\w+)\}/g, (_, k) => (vars[k] !== undefined ? String(vars[k]) : `{${k}}`));
};

const I18nContext = createContext(null);

export const I18nProvider = ({ children }) => {
  const [locale, setLocaleState] = useState(() => readStoredLocale());
  const [displayLocale, setDisplayLocaleState] = useState(() => readStoredDisplayLocale());

  // Apply locale to <html lang> for accessibility + browser hints
  useEffect(() => {
    if (isBrowser) document.documentElement.setAttribute("lang", locale);
  }, [locale]);

  const setLocale = useCallback((next) => {
    if (!SUPPORTED_LOCALES.includes(next)) return;
    writeStoredLocale(next);
    setLocaleState(next);
  }, []);

  const setDisplayLocale = useCallback((next) => {
    if (!SUPPORTED_DISPLAY_LOCALES.includes(next)) return;
    writeStoredDisplayLocale(next);
    setDisplayLocaleState(next);
  }, []);

  const t = useCallback(
    (key, vars) => {
      const bundle = BUNDLES[locale] || BUNDLES.en;
      const fallback = BUNDLES.en;
      let val = resolve(bundle, key);
      if (val === undefined) val = resolve(fallback, key);
      if (val === undefined) return key;
      return interpolate(val, vars);
    },
    [locale],
  );

  const value = useMemo(
    () => ({ locale, displayLocale, setLocale, setDisplayLocale, t }),
    [locale, displayLocale, setLocale, setDisplayLocale, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useT = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useT must be used within <I18nProvider>");
  return ctx;
};

export const LOCALE_META = {
  en: { name: "English", short: "EN", flag: "🇬🇧" },
  de: { name: "Deutsch", short: "DE", flag: "🇩🇪" },
  hu: { name: "Magyar", short: "HU", flag: "🇭🇺" },
};
